import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, SITE_ORIGIN } from "@/lib/api";
import { FireIcon, ArrowRightIcon } from "@phosphor-icons/react";

const fileUrl = (p) =>
  p && p.startsWith("http") ? p : p ? `${SITE_ORIGIN}/api/files/${p}` : null;

export default function TrendingThisWeek() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/trending/week", { params: { limit: 6 } });
        if (alive) setData(data);
      } catch {
        if (alive) setData({ listings: [], categories: [] });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Hide entirely if DB has nothing trending yet.
  if (data && data.listings.length === 0 && data.categories.length === 0) {
    return null;
  }

  return (
    <section
      className="bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50 border-y border-amber-100"
      data-testid="home-trending-week"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-sm font-bold text-orange-600 mb-2">
              <FireIcon size={16} weight="fill" />
              Trending this week
            </div>
            <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter leading-tight max-w-2xl">
              What everyone's bidding on right now.
            </h2>
          </div>
          <Link
            to="/browse"
            className="text-sm font-semibold text-orange-700 hover:underline"
            data-testid="trending-view-all"
          >
            Browse all <ArrowRightIcon size={14} className="inline ml-1" />
          </Link>
        </div>

        {data?.categories?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="trending-categories">
            {data.categories.map((c) => (
              <Link
                key={c.category}
                to={`/browse?category=${encodeURIComponent(c.category)}`}
                className="inline-flex items-center gap-2 bg-white border border-orange-200 rounded-full px-3.5 py-1.5 text-sm font-semibold hover:border-orange-400 transition"
                data-testid={`trending-cat-${c.category.toLowerCase().replace(/\s/g, "-")}`}
              >
                <span className="text-orange-600 font-bold text-xs">
                  {c.velocity_label}
                </span>
                <span className="text-zinc-900">{c.category}</span>
              </Link>
            ))}
          </div>
        )}

        {data?.listings?.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {data.listings.map((l) => (
              <Link
                key={l.id}
                to={`/listing/${l.id}`}
                className="group rounded-2xl overflow-hidden bg-white border border-zinc-200 hover:border-orange-400 hover:shadow-lg transition"
                data-testid={`trending-listing-${l.id}`}
              >
                <div className="aspect-square bg-zinc-100 overflow-hidden">
                  {l.images?.[0] && (
                    <img
                      src={fileUrl(l.images[0])}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition"
                    />
                  )}
                </div>
                <div className="p-2.5">
                  <div className="text-xs font-semibold text-zinc-900 line-clamp-2 leading-snug min-h-[2.4em]">
                    {l.title}
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="font-black text-sm text-zinc-900">
                      ${(l.current_bid || l.price || 0).toFixed(0)}
                    </div>
                    {l.bid_count > 0 && (
                      <div className="text-[10px] text-orange-600 font-bold">
                        {l.bid_count} bid{l.bid_count === 1 ? "" : "s"}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
